// Seguimiento de pedidos — rol "administracion" (Marcela). SOLO LECTURA.
// Muestra los pedidos agrupados por estado (todo el ciclo) y un atajo a
// Facturación con contador de pendientes (hace de aviso). Al tocar un pedido
// abre un detalle de solo lectura (composición + cliente), sin acciones.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";

const ESTADO_META = {
  PENDIENTE_ASIGNAR: { label: "Pendiente de asignar", icon: "📥", color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  ASIGNADO:          { label: "Asignado",             icon: "👤", color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  EN_PREPARACION:    { label: "En preparación",       icon: "🔧", color: "#854d0e", bg: "#fefce8", border: "#fde047" },
  CON_ERROR:         { label: "Con error",            icon: "🛑", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  PREPARADO:         { label: "Preparado",            icon: "✅", color: "#15803d", bg: "#f0fdf4", border: "#86efac" },
  CONTROLADO:        { label: "Controlado",           icon: "🔍", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
  DESPACHADO:        { label: "Despachado",           icon: "🚚", color: "#185fa5", bg: "#eff6ff", border: "#bfdbfe" },
  ENTREGADO:         { label: "Entregado",            icon: "📦", color: "#15803d", bg: "#f0fdf4", border: "#86efac" },
};
// Estados virtuales SOLO para R8: el despacho se divide en "Por facturar" (Marcela
// factura acá mismo) y "Para entregar" (ya facturado, esperando la entrega).
ESTADO_META.POR_FACTURAR  = { label: "Por facturar",  icon: "🧾", color: "#b45309", color2: "#b91c1c", bg: "#fffbeb", border: "#fde68a" };
ESTADO_META.PARA_ENTREGAR = { label: "Para entregar", icon: "📦", color: "#0369a1", bg: "#eff6ff", border: "#bfdbfe" };

const ORDEN_ISABELA = ["PENDIENTE_ASIGNAR", "ASIGNADO", "EN_PREPARACION", "CON_ERROR", "PREPARADO", "CONTROLADO", "DESPACHADO", "ENTREGADO"];
// En R8, DESPACHADO se reemplaza por POR_FACTURAR + PARA_ENTREGAR.
const ORDEN_R8 = ["PENDIENTE_ASIGNAR", "ASIGNADO", "EN_PREPARACION", "CON_ERROR", "PREPARADO", "CONTROLADO", "POR_FACTURAR", "PARA_ENTREGAR", "ENTREGADO"];

export default function SeguimientoAdmin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useApp();
  const [pedidos, setPedidos] = useState([]);
  const [despachados, setDespachados] = useState([]);
  const [entregados, setEntregados] = useState([]);
  const [colapsados, setColapsados] = useState(() => new Set());
  const [deposito, setDeposito] = useState("R8"); // R8 | ISABELA
  const [busy, setBusy] = useState(null); // id del pedido facturándose
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "pedidos"), s =>
      setPedidos(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, "pedidos_despachados"), s =>
      setDespachados(s.docs.map(d => ({ id: d.id, _col: "pedidos_despachados", ...d.data(), estado: "DESPACHADO" }))));
    const u3 = onSnapshot(collection(db, "pedidos_entregados"), s =>
      setEntregados(s.docs.map(d => ({ id: d.id, _col: "pedidos_entregados", ...d.data(), estado: "ENTREGADO" }))));
    return () => { u1(); u2(); u3(); };
  }, []);

  const esR8 = deposito === "R8";

  // Marcar un pedido como facturado (solo R8). Actualiza el doc en su colección.
  async function marcarFacturado(p) {
    if (busy || !esR8) return;
    setBusy(p.id);
    try {
      await updateDoc(doc(db, p._col || "pedidos_despachados", p.id), {
        facturado: true,
        facturadoAt: serverTimestamp(),
        facturadoPor: user?.uid || null,
      });
      toast?.success?.(`#${p.numero || p.id} facturado ✓`);
    } catch (e) {
      console.error("[seguimiento] facturar", e);
      toast?.error?.("No se pudo marcar como facturado");
    } finally {
      setBusy(null);
    }
  }

  // Revertir una factura hecha por error (solo R8 y mientras NO esté entregado).
  async function desmarcarFacturado(p) {
    if (busy || !esR8) return;
    if (p.estado === "ENTREGADO") {
      toast?.error?.("El pedido ya fue entregado; no se puede des-facturar.");
      return;
    }
    if (!window.confirm(`¿Traer el #${p.numero || p.id} de vuelta a "Por facturar"?`)) return;
    setBusy(p.id);
    try {
      await updateDoc(doc(db, p._col || "pedidos_despachados", p.id), {
        facturado: false,
        facturadoAt: null,
        facturadoPor: null,
      });
      toast?.success?.(`#${p.numero || p.id} vuelto a "Por facturar"`);
    } catch (e) {
      console.error("[seguimiento] des-facturar", e);
      toast?.error?.("No se pudo revertir la facturación");
    } finally {
      setBusy(null);
    }
  }

  // Un registro por pedido, en su estado más avanzado (entregado > despachado > activo),
  // filtrado por el depósito seleccionado.
  const todos = useMemo(() => {
    const map = new Map();
    for (const p of pedidos) if (!["ANULADO", "CANCELADO"].includes(p.estado)) map.set(p.id, p);
    for (const p of despachados) map.set(p.id, p);
    for (const p of entregados) map.set(p.id, p);
    return [...map.values()].filter(p => String(p.deposito || "").toUpperCase() === deposito);
  }, [pedidos, despachados, entregados, deposito]);

  const ORDEN = esR8 ? ORDEN_R8 : ORDEN_ISABELA;

  const porEstado = useMemo(() => {
    const out = {};
    for (const p of todos) {
      let e = p.estado || "PENDIENTE_ASIGNAR";
      // R8: el despacho se separa en "Por facturar" / "Para entregar".
      if (esR8 && e === "DESPACHADO") {
        e = p.facturado === true ? "PARA_ENTREGAR" : "POR_FACTURAR";
      }
      (out[e] ||= []).push(p);
    }
    return out;
  }, [todos, esR8]);

  // Pendientes de facturar (para el atajo/aviso), del depósito seleccionado.
  const pendientesFacturar = useMemo(
    () => [...despachados, ...entregados]
      .filter(p => p.facturado !== true && String(p.deposito || "").toUpperCase() === deposito).length,
    [despachados, entregados, deposito]
  );

  const toggle = (e) => setColapsados(prev => {
    const n = new Set(prev); n.has(e) ? n.delete(e) : n.add(e); return n;
  });

  return (
    <div style={{ padding: "16px", maxWidth: isDesktop ? "100%" : "760px", margin: "0 auto", paddingBottom: "40px" }}>
      <button onClick={() => navigate("/")} style={{ background: "none", border: "none", color: "#0f766e", fontWeight: 600, fontSize: "0.85rem", padding: 0, marginBottom: "12px", cursor: "pointer" }}>
        ← Volver
      </button>

      <h2 style={{ margin: "0 0 4px", fontSize: "1.2rem", fontWeight: 800 }}>👁️ Seguimiento de pedidos</h2>
      <p style={{ margin: "0 0 12px", fontSize: "0.85rem", color: "#64748b" }}>
        Estado de todos los pedidos (solo lectura). Tocá uno para ver su detalle.
      </p>

      {/* Selector de depósito */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
        {["R8", "ISABELA"].map(dep => {
          const active = deposito === dep;
          return (
            <button key={dep} onClick={() => setDeposito(dep)}
              style={{
                padding: "6px 16px", borderRadius: "999px", cursor: "pointer", fontWeight: 700, fontSize: "0.8rem",
                border: `1.5px solid ${active ? "#0f766e" : "#e2e8f0"}`,
                background: active ? "#0f766e" : "#fff",
                color: active ? "#fff" : "#64748b",
              }}>
              🏭 {dep}
            </button>
          );
        })}
      </div>

      {/* ── ISABELA: aviso de solo lectura (R8 no lleva banner; la columna
             "Por facturar" ya muestra el contador en rojo como notificación) ── */}
      {deposito !== "R8" && (
        <div
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: "12px",
            padding: "14px 16px", borderRadius: "14px", textAlign: "left",
            border: "1.5px solid #e2e8f0", background: "#f8fafc", marginBottom: "18px",
          }}
        >
          <span style={{ fontSize: "1.6rem", lineHeight: 1 }}>👁️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: "0.95rem", color: "#475569" }}>
              ISABELA — solo lectura
            </div>
            <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Podés ver el estado de los pedidos, pero ISABELA no se factura desde acá.
            </div>
          </div>
        </div>
      )}

      {/* ── Estados ── */}
      {isDesktop ? (
        /* Desktop: kanban horizontal (una columna por estado, como Ventas) */
        <div style={{ display: "flex", gap: "10px", overflowX: "auto", alignItems: "flex-start", paddingBottom: "8px" }}>
          {ORDEN.map(e => {
            const meta = ESTADO_META[e] || { label: e, icon: "📋", color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" };
            const items = (porEstado[e] || []).slice().sort((a, b) => (b.numero || 0) - (a.numero || 0));
            return (
              <div key={e} style={{ flex: "1 0 200px", minWidth: "200px", maxWidth: "280px", display: "flex", flexDirection: "column", background: meta.bg, border: `1.5px solid ${meta.border}`, borderRadius: "12px", overflow: "hidden" }}>
                <div style={{ padding: "10px 12px", borderBottom: `1px solid ${meta.border}`, display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>{meta.icon}</span>
                  <span style={{ flex: 1, fontWeight: 700, fontSize: "0.78rem", color: meta.color }}>{meta.label}</span>
                  {e === "POR_FACTURAR" && items.length > 0 ? (
                    <span style={{ background: "#dc2626", color: "#fff", fontSize: "0.72rem", fontWeight: 800, padding: "1px 8px", borderRadius: "999px", boxShadow: "0 0 0 2px #fff" }}>{items.length}</span>
                  ) : (
                    <span style={{ background: items.length ? meta.color + "22" : "#f1f5f9", color: items.length ? meta.color : "#94a3b8", fontSize: "0.72rem", fontWeight: 700, padding: "1px 8px", borderRadius: "999px" }}>{items.length}</span>
                  )}
                </div>
                <div style={{ padding: "8px", display: "flex", flexDirection: "column", gap: "6px", maxHeight: "70vh", overflowY: "auto" }}>
                  {items.length === 0 ? (
                    <p style={{ textAlign: "center", color: "#cbd5e1", fontSize: "0.74rem", padding: "12px 0", margin: 0 }}>—</p>
                  ) : items.map(p => (
                    <div key={p.id} onClick={() => navigate(`/administracion/seguimiento/${encodeURIComponent(p.id)}`)}
                      style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "8px 10px", cursor: "pointer" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#0f172a" }}>#{p.numero || p.id}</div>
                      <div style={{ fontSize: "0.76rem", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.cliente || "—"}</div>
                      {p.metodoEntrega && <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "1px 6px", borderRadius: "999px", marginTop: "3px", display: "inline-block" }}>{p.metodoEntrega}</span>}
                      {e === "POR_FACTURAR" && (
                        <button
                          onClick={(ev) => { ev.stopPropagation(); marcarFacturado(p); }}
                          disabled={busy === p.id}
                          style={{ marginTop: "6px", width: "100%", padding: "6px 8px", borderRadius: "8px", border: "none", background: busy === p.id ? "#cbd5e1" : "#0f766e", color: "#fff", fontWeight: 700, fontSize: "0.72rem", cursor: busy === p.id ? "default" : "pointer" }}
                        >
                          {busy === p.id ? "…" : "🧾 Marcar facturado"}
                        </button>
                      )}
                      {e === "PARA_ENTREGAR" && (
                        <button
                          onClick={(ev) => { ev.stopPropagation(); desmarcarFacturado(p); }}
                          disabled={busy === p.id}
                          title="Traer de vuelta a Por facturar"
                          style={{ marginTop: "6px", width: "100%", padding: "5px 8px", borderRadius: "8px", border: "1px solid #fca5a5", background: "#fff", color: "#b91c1c", fontWeight: 700, fontSize: "0.68rem", cursor: busy === p.id ? "default" : "pointer" }}
                        >
                          {busy === p.id ? "…" : "↩ Des-facturar"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Mobile: secciones colapsables (solo estados con pedidos) */
        ORDEN.filter(e => (porEstado[e] || []).length > 0).map(e => {
          const meta = ESTADO_META[e] || { label: e, icon: "📋", color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" };
          const items = porEstado[e];
          const abierto = !colapsados.has(e);
          return (
            <div key={e} style={{ marginBottom: "8px" }}>
              <button
                onClick={() => toggle(e)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "10px",
                  padding: "12px 14px", cursor: "pointer", textAlign: "left",
                  background: abierto ? meta.bg : "#fff",
                  border: `1.5px solid ${abierto ? meta.border : "#e2e8f0"}`,
                  borderRadius: abierto ? "12px 12px 0 0" : "12px",
                }}
              >
                <span style={{ fontSize: "1rem" }}>{meta.icon}</span>
                <span style={{ flex: 1, fontWeight: 700, fontSize: "0.88rem", color: meta.color }}>{meta.label}</span>
                {e === "POR_FACTURAR" && items.length > 0 ? (
                  <span style={{ background: "#dc2626", color: "#fff", fontSize: "0.78rem", fontWeight: 800, padding: "2px 9px", borderRadius: "999px" }}>{items.length}</span>
                ) : (
                  <span style={{ background: meta.color + "22", color: meta.color, fontSize: "0.78rem", fontWeight: 700, padding: "2px 9px", borderRadius: "999px" }}>{items.length}</span>
                )}
                <span style={{ color: meta.color, fontSize: "0.85rem", transform: abierto ? "none" : "rotate(-90deg)" }}>▾</span>
              </button>
              {abierto && (
                <div style={{ border: `1.5px solid ${meta.border}`, borderTop: "none", borderRadius: "0 0 12px 12px", background: "#fff", padding: "6px" }}>
                  {items
                    .slice()
                    .sort((a, b) => (b.numero || 0) - (a.numero || 0))
                    .map(p => (
                      <div
                        key={p.id}
                        onClick={() => navigate(`/administracion/seguimiento/${encodeURIComponent(p.id)}`)}
                        style={{ padding: "10px 12px", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: "0.86rem", color: "#0f172a" }}>#{p.numero || p.id}</div>
                          <div style={{ fontSize: "0.8rem", color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.cliente || "—"}</div>
                        </div>
                        {p.metodoEntrega && (
                          <span style={{ fontSize: "0.64rem", fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "2px 7px", borderRadius: "999px", flexShrink: 0 }}>{p.metodoEntrega}</span>
                        )}
                        <span style={{ color: "#cbd5e1", flexShrink: 0 }}>›</span>
                        {e === "POR_FACTURAR" && (
                          <button
                            onClick={(ev) => { ev.stopPropagation(); marcarFacturado(p); }}
                            disabled={busy === p.id}
                            style={{ flexBasis: "100%", marginTop: "6px", padding: "9px", borderRadius: "8px", border: "none", background: busy === p.id ? "#cbd5e1" : "#0f766e", color: "#fff", fontWeight: 700, fontSize: "0.8rem", cursor: busy === p.id ? "default" : "pointer" }}
                          >
                            {busy === p.id ? "…" : "🧾 Marcar facturado"}
                          </button>
                        )}
                        {e === "PARA_ENTREGAR" && (
                          <button
                            onClick={(ev) => { ev.stopPropagation(); desmarcarFacturado(p); }}
                            disabled={busy === p.id}
                            style={{ flexBasis: "100%", marginTop: "6px", padding: "8px", borderRadius: "8px", border: "1px solid #fca5a5", background: "#fff", color: "#b91c1c", fontWeight: 700, fontSize: "0.76rem", cursor: busy === p.id ? "default" : "pointer" }}
                          >
                            {busy === p.id ? "…" : "↩ Des-facturar"}
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })
      )}

      {todos.length === 0 && (
        <p style={{ color: "#94a3b8", fontSize: "0.9rem", textAlign: "center", padding: "30px 0" }}>
          No hay pedidos en {deposito}.
        </p>
      )}
    </div>
  );
}
